import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Tabs,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  LinkOutlined,
  MailOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { employeesApi } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { scrollToFirstError } from '../../utils/formHelpers';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmailDomain = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('@') ? trimmed : `@${trimmed}`;
};

const EmployeeInviteModal = ({ open, onClose }) => {
  const [emailForm] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('email');
  const [emailLoading, setEmailLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [inviteLinkResult, setInviteLinkResult] = useState(null);

  const handleClose = () => {
    emailForm.resetFields();
    linkForm.resetFields();
    setInviteLinkResult(null);
    setActiveTab('email');
    onClose?.();
  };

  const handleCopyLink = async (link) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      message.success('لینک در کلیپ‌بورد کپی شد');
    } catch {
      message.warning('کپی خودکار ممکن نبود؛ لینک را دستی انتخاب کنید');
    }
  };

  const handleSendEmailInvite = async () => {
    try {
      const values = await emailForm.validateFields();
      const rawEmails = (values.emails || [])
        .map((item) => String(item?.address || '').trim())
        .filter(Boolean);

      if (!rawEmails.length) {
        message.error('حداقل یک ایمیل وارد کنید');
        return;
      }

      const invalid = rawEmails.find((addr) => !EMAIL_PATTERN.test(addr));
      if (invalid) {
        message.error(`ایمیل نامعتبر: ${invalid}`);
        return;
      }

      setEmailLoading(true);
      const res = await employeesApi.sendInvite(rawEmails, values.message || '');
      message.success(res?.message || 'دعوت‌نامه با موفقیت ارسال شد');
      emailForm.resetFields();
      emailForm.setFieldsValue({ emails: [{ address: '' }] });
      handleClose();
    } catch (error) {
      if (error?.errorFields?.length) {
        scrollToFirstError(emailForm);
      } else {
        message.error(error?.message || 'خطا در ارسال دعوت‌نامه');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCreateInviteLink = async () => {
    try {
      const values = await linkForm.validateFields();
      const allowEmail = values.allow_email || 'any';
      let emailDomain = null;

      if (allowEmail === 'selected') {
        emailDomain = normalizeEmailDomain(values.email_domain);
        if (!emailDomain || emailDomain === '@') {
          message.error('دامنه ایمیل را وارد کنید');
          return;
        }
      }

      setLinkLoading(true);
      setInviteLinkResult(null);
      const res = await employeesApi.createInviteLink({
        allowEmail,
        emailDomain,
      });

      const data = res?.data || {};
      setInviteLinkResult({
        link: data.link || '',
        invitationCode: data.invitation_code || '',
        emailRestriction: data.email_restriction,
        message: res?.message || 'لینک دعوت‌نامه با موفقیت ایجاد شد.',
      });
      message.success(res?.message || 'لینک دعوت‌نامه با موفقیت ایجاد شد');
    } catch (error) {
      if (error?.errorFields?.length) {
        scrollToFirstError(linkForm);
      } else {
        message.error(error?.message || 'خطا در ایجاد لینک دعوت');
      }
    } finally {
      setLinkLoading(false);
    }
  };

  const emailTab = (
    <Form
      form={emailForm}
      layout="vertical"
      initialValues={{ emails: [{ address: '' }], message: '' }}
      onFinish={handleSendEmailInvite}
      className="pt-2"
    >
      <Paragraph type="secondary" className="!mb-4 text-sm">
        ایمیل کارمندان را وارد کنید تا دعوت‌نامه برایشان ارسال شود.
      </Paragraph>

      <Form.List name="emails">
        {(fields, { add, remove }) => (
          <div className="space-y-3 mb-4">
            {fields.map((field, index) => (
              <div key={field.key} className="flex flex-col sm:flex-row gap-2 sm:items-start">
                <Form.Item
                  {...field}
                  name={[field.name, 'address']}
                  label={index === 0 ? 'ایمیل' : null}
                  rules={[
                    { required: true, message: 'ایمیل را وارد کنید' },
                    { type: 'email', message: 'فرمت ایمیل نامعتبر است' },
                  ]}
                  className="flex-1 !mb-0"
                >
                  <Input
                    placeholder="example@company.com"
                    prefix={<MailOutlined className="text-gray-400" />}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
                {fields.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(field.name)}
                    className="sm:mt-[30px]"
                    aria-label="حذف ایمیل"
                  />
                )}
              </div>
            ))}
            <Button
              type="dashed"
              onClick={() => add({ address: '' })}
              block
              icon={<PlusOutlined />}
              style={{ fontFamily: 'CustomFont' }}
            >
              افزودن ایمیل دیگر
            </Button>
          </div>
        )}
      </Form.List>

      <Form.Item
        name="message"
        label="پیام دعوت (اختیاری)"
        extra="این متن همراه دعوت‌نامه برای کارمند ارسال می‌شود."
      >
        <TextArea
          rows={3}
          placeholder="به تیم ما بپیوندید..."
          maxLength={500}
          showCount
          style={{ fontFamily: 'CustomFont' }}
        />
      </Form.Item>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <Button onClick={handleClose} style={{ fontFamily: 'CustomFont' }}>
          انصراف
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={emailLoading}
          icon={<MailOutlined />}
          className={PANEL_PRIMARY_BTN_CLASS}
          style={{ fontFamily: 'CustomFont' }}
        >
          ارسال دعوت‌نامه
        </Button>
      </div>
    </Form>
  );

  const linkTab = (
    <Form
      form={linkForm}
      layout="vertical"
      initialValues={{ allow_email: 'any', email_domain: '' }}
      onFinish={handleCreateInviteLink}
      className="pt-2"
    >
      <Paragraph type="secondary" className="!mb-4 text-sm">
        یک لینک دعوت یک‌بار مصرف بسازید و آن را با کارمندان به اشتراک بگذارید.
      </Paragraph>

      <Form.Item name="allow_email" label="محدودیت ایمیل">
        <Radio.Group className="flex flex-col gap-2 sm:flex-row sm:gap-6">
          <Radio value="any" style={{ fontFamily: 'CustomFont' }}>
            همه دامنه‌های ایمیل
          </Radio>
          <Radio value="selected" style={{ fontFamily: 'CustomFont' }}>
            فقط دامنه مشخص
          </Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.allow_email !== cur.allow_email}>
        {({ getFieldValue }) =>
          getFieldValue('allow_email') === 'selected' ? (
            <Form.Item
              name="email_domain"
              label="دامنه مجاز"
              rules={[{ required: true, message: 'دامنه ایمیل را وارد کنید' }]}
              extra="مثال: roomeet.ir — فقط ایمیل‌های این دامنه می‌توانند ثبت‌نام کنند."
            >
              <Input
                addonBefore="@"
                placeholder="roomeet.ir"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
          ) : null
        }
      </Form.Item>

      {inviteLinkResult?.link && (
        <Alert
          type="success"
          showIcon
          className="mb-4"
          message={inviteLinkResult.message}
          description={
            <div className="mt-2 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Input
                  readOnly
                  value={inviteLinkResult.link}
                  className="flex-1"
                  style={{ fontFamily: 'CustomFont', direction: 'ltr', textAlign: 'left' }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyLink(inviteLinkResult.link)}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  کپی لینک
                </Button>
              </div>
              {inviteLinkResult.emailRestriction != null && inviteLinkResult.emailRestriction !== '' && (
                <Text type="secondary" className="text-xs block">
                  محدودیت: {inviteLinkResult.emailRestriction}
                </Text>
              )}
            </div>
          }
        />
      )}

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <Button onClick={handleClose} style={{ fontFamily: 'CustomFont' }}>
          بستن
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={linkLoading}
          icon={<LinkOutlined />}
          className={PANEL_PRIMARY_BTN_CLASS}
          style={{ fontFamily: 'CustomFont' }}
        >
          {inviteLinkResult?.link ? 'ایجاد لینک جدید' : 'ایجاد لینک دعوت'}
        </Button>
      </div>
    </Form>
  );

  const tabItems = [
    {
      key: 'email',
      label: (
        <span style={{ fontFamily: 'CustomFont' }}>
          <MailOutlined className="ml-1" />
          دعوت با ایمیل
        </span>
      ),
      children: emailTab,
    },
    {
      key: 'link',
      label: (
        <span style={{ fontFamily: 'CustomFont' }}>
          <LinkOutlined className="ml-1" />
          دعوت با لینک
        </span>
      ),
      children: linkTab,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={560}
      destroyOnClose
      centered
      title={
        <div className="flex items-center gap-2">
          <UserAddOutlined className="text-indigo-600" />
          <span style={{ fontFamily: 'CustomFont' }}>دعوت از کارمند</span>
        </div>
      }
      styles={{ body: { paddingTop: 8, fontFamily: 'CustomFont' } }}
      className="employee-invite-modal"
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setInviteLinkResult(null);
        }}
        items={tabItems}
        className="employee-invite-tabs"
      />
    </Modal>
  );
};

export default EmployeeInviteModal;
